import React, { useEffect, useState } from "react";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { RESULTS_ENABLED } from "../lib/features";

const STORAGE_KEY_ID = "ambasada.memberId";
const STORAGE_KEY_NAME = "ambasada.memberName";
const ID_ALPHABET = "abcdefghijklmnopqrstuvwxyz0123456789";
const ID_LENGTH = 8;

const generateMemberId = () => {
    const buf = new Uint8Array(ID_LENGTH);
    if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(buf);
    } else {
        for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    }
    let out = "";
    for (let i = 0; i < buf.length; i++) {
        out += ID_ALPHABET[buf[i] % ID_ALPHABET.length];
    }
    return out;
};

const readLocal = (key) => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeLocal = (key, value) => {
    try {
        if (value == null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, value);
    } catch {}
};

const MemberBanner = ({ track }) => {
    const resultsHref = track ? `/t/${track}/results` : "/results";
    const [memberId, setMemberId] = useState(null);
    const [memberName, setMemberName] = useState("");
    const [draft, setDraft] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const id = readLocal(STORAGE_KEY_ID);
        const cachedName = readLocal(STORAGE_KEY_NAME);
        if (id) {
            setMemberId(id);
            if (cachedName) setMemberName(cachedName);
            (async () => {
                try {
                    const snap = await getDoc(doc(db, "members", id));
                    if (snap.exists()) {
                        const n = snap.data().name;
                        if (typeof n === "string" && n.length > 0) {
                            setMemberName(n);
                            writeLocal(STORAGE_KEY_NAME, n);
                        }
                    }
                } catch {}
            })();
        }
    }, []);

    const handleSave = async () => {
        const name = draft.trim().slice(0, 40);
        if (!name) return;
        setSaving(true);
        try {
            const id = memberId || generateMemberId();
            await setDoc(
                doc(db, "members", id),
                { name, createdAt: serverTimestamp(), active: true },
                { merge: true }
            );
            writeLocal(STORAGE_KEY_ID, id);
            writeLocal(STORAGE_KEY_NAME, name);
            setMemberId(id);
            setMemberName(name);
            setIsEditing(false);
            setDraft("");
        } catch (err) {
            console.error("Failed to save member", err);
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        writeLocal(STORAGE_KEY_ID, null);
        writeLocal(STORAGE_KEY_NAME, null);
        setMemberId(null);
        setMemberName("");
        setIsEditing(false);
        setDraft("");
    };

    const handleSkip = () => {
        setDismissed(true);
    };

    // The banner's whole pitch is the results page; without it, no banner.
    if (!RESULTS_ENABLED) return null;

    if (memberId && memberName && !isEditing) {
        return (
            <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-6">
                <div className="rounded-2xl bg-brand-ink text-white p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-brand">
                    <div className="flex items-center gap-3">
                        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-red flex items-center justify-center font-brand font-bold text-white text-lg">
                            {memberName.charAt(0).toUpperCase()}
                        </span>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-red">
                                You're in
                            </div>
                            <div className="font-brand text-lg sm:text-xl font-bold leading-tight">
                                Sharing as {memberName}<span className="text-brand-red">.</span>
                            </div>
                            <div className="text-xs text-white/60 mt-0.5">
                                <a href={`/m/${memberId}`} className="underline hover:text-white">See your page</a> or browse <a href={resultsHref} className="underline hover:text-white">{track ? "track results" : "all results"}</a>.
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => { setDraft(memberName); setIsEditing(true); }}
                            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-semibold transition-colors"
                        >
                            Edit name
                        </button>
                        <button
                            type="button"
                            onClick={handleClear}
                            className="text-white/60 hover:text-white transition-colors"
                        >
                            Clear
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (dismissed && !isEditing) return null;

    return (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-6">
            <div className="rounded-2xl bg-brand-red text-white p-6 sm:p-8 shadow-brand relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-white/10 pointer-events-none" aria-hidden="true" />
                <div className="absolute -bottom-16 -left-10 w-56 h-56 rounded-full bg-white/5 pointer-events-none" aria-hidden="true" />
                <div className="relative flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-white/80 mb-2">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                            New
                        </div>
                        <h2 className="font-brand text-2xl sm:text-3xl lg:text-[32px] font-bold leading-tight">
                            Get credit for your shares<span className="text-white/80">.</span>
                        </h2>
                        <p className="mt-2 text-sm sm:text-base text-white/90 max-w-2xl">
                            Add your name and every click your shares drive lands on the <a href={resultsHref} className="underline font-semibold hover:text-white">results page</a> next to it. Optional, change or remove it anytime.
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 lg:flex-shrink-0">
                        <input
                            type="text"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
                            placeholder="Your name"
                            maxLength={40}
                            autoFocus
                            className="px-4 py-3 rounded-xl text-base bg-white text-brand-ink placeholder:text-brand-muted focus:outline-none focus:ring-4 focus:ring-white/40 w-full sm:w-56 font-medium"
                        />
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || draft.trim().length === 0}
                            className="px-6 py-3 rounded-xl bg-brand-ink text-white text-base font-semibold hover:bg-black disabled:opacity-50 transition-colors whitespace-nowrap"
                        >
                            {saving ? "Saving..." : "Add my name"}
                        </button>
                        {!memberId && (
                            <button
                                type="button"
                                onClick={handleSkip}
                                className="px-3 py-2 text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap"
                            >
                                Not now
                            </button>
                        )}
                        {memberId && isEditing && (
                            <button
                                type="button"
                                onClick={() => { setIsEditing(false); setDraft(""); }}
                                className="px-3 py-2 text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap"
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberBanner;
